import { Criticidad } from '../../domain/value-objects/Criticidad';
import { Dominio } from '../../domain/value-objects/Dominio';
import { registrarError, traducirErrorSupabase } from '../../domain/errores';
import { aFecha, aNumero, aObjeto, aTexto } from '../mappers/conversiones';
import { supabase } from '../supabase/cliente';
import { ambitoSesion } from './AmbitoSesion';

/** Aviso que no se pudo entregar, con el motivo. */
export interface FalloAviso {
  fecha: Date | null;
  clienteId: number | null;
  clienteNombre: string | null;
  nodoId: number | null;
  nodoNombre: string | null;
  sensorId: number | null;
  sensorNombre: string | null;
  alarmaId: number | null;
  /** Código técnico del motivo; sirve para agrupar y filtrar. */
  codigoMotivo: string | null;
  /** Motivo en palabras. Si la vista no lo trae, se cae al código. */
  motivo: string;
  dominio: Dominio;
  severidad: Criticidad;
  referencia: string | null;
  /** Datos técnicos en crudo del suceso. */
  detalles: Record<string, unknown>;
}

export interface FiltrosBitacora {
  clienteId?: number | null;
  nodoId?: number | null;
  sensorId?: number | null;
  codigoMotivo?: string | null;
  desde?: string | null;
  hasta?: string | null;
}

/** Motivo distinto encontrado en la bitácora, para poblar el filtro. */
export interface MotivoFallo {
  codigo: string;
  etiqueta: string;
}

/** Sólo interesan los fallos de entrega; la vista guarda más tipos de suceso. */
const SUCESO = 'NOTIF_OUTBOX_FAIL';

/** Categoría con la que el servidor lleva la cuenta de lo ya visto. */
const CATEGORIA = 'notif_outbox_fail';

/** Cuántos fallos se traen; los últimos 200 es lo que se revisa en la práctica. */
const LIMITE = 200;

function mapear(fila: Record<string, unknown>): FalloAviso {
  const codigo = aTexto(fila.reason_code);
  const etiqueta = aTexto(fila.motivo_label_es);

  return {
    fecha: aFecha(fila.ts),
    clienteId: aNumero(fila.cliente_id),
    clienteNombre: aTexto(fila.cliente_nombre),
    nodoId: aNumero(fila.nodo_id),
    nodoNombre: aTexto(fila.nodo_nombre),
    sensorId: aNumero(fila.sensor_id),
    sensorNombre: aTexto(fila.sensor_nombre),
    alarmaId: aNumero(fila.alarma_id),
    codigoMotivo: codigo,
    motivo: etiqueta ?? codigo ?? 'Motivo desconocido',
    dominio: Dominio.from(aTexto(fila.dominio)),
    severidad: Criticidad.from(aTexto(fila.severidad)),
    referencia: aTexto(fila.ref),
    detalles: aObjeto(fila.details),
  };
}

/**
 * Bitácora de avisos que no llegaron.
 *
 * Es la pantalla a la que se acude cuando alguien dice «no me avisaron»: aquí
 * está el motivo técnico traducido a palabras, junto al sensor y la alarma que
 * lo provocaron.
 */
export class RepositorioBitacora {
  async listar(filtros: FiltrosBitacora = {}): Promise<FalloAviso[]> {
    let consulta = supabase
      .from('vw_ops_notif_fail_ui')
      .select(
        'ts, cliente_id, cliente_nombre, nodo_id, nodo_nombre, sensor_id, sensor_nombre, alarma_id, event, ref, reason_code, motivo_label_es, dominio, severidad, details',
      )
      .eq('event', SUCESO);

    consulta = ambitoSesion.aplicar(consulta);
    if (filtros.clienteId) consulta = consulta.eq('cliente_id', filtros.clienteId);
    if (filtros.nodoId) consulta = consulta.eq('nodo_id', filtros.nodoId);
    if (filtros.sensorId) consulta = consulta.eq('sensor_id', filtros.sensorId);
    if (filtros.codigoMotivo) consulta = consulta.eq('reason_code', filtros.codigoMotivo);

    if (filtros.desde) {
      consulta = consulta.gte('ts', new Date(`${filtros.desde}T00:00:00`).toISOString());
    }
    if (filtros.hasta) {
      consulta = consulta.lte('ts', new Date(`${filtros.hasta}T23:59:59.999`).toISOString());
    }

    const { data, error } = await consulta.order('ts', { ascending: false }).limit(LIMITE);
    if (error) throw traducirErrorSupabase(error);

    return ((data ?? []) as Record<string, unknown>[]).map(mapear);
  }

  /**
   * Motivos distintos que aparecen en la bitácora.
   *
   * Se sacan de los propios datos en vez de mantener una lista fija: si el
   * backend añade un motivo nuevo, el filtro lo recoge solo.
   */
  async motivos(): Promise<MotivoFallo[]> {
    const { data, error } = await supabase
      .from('vw_ops_notif_fail_ui')
      .select('reason_code, motivo_label_es')
      .eq('event', SUCESO)
      .order('motivo_label_es', { ascending: true });

    if (error) throw traducirErrorSupabase(error);

    const unicos = new Map<string, MotivoFallo>();

    for (const fila of (data ?? []) as Record<string, unknown>[]) {
      const codigo = aTexto(fila.reason_code);
      if (!codigo || unicos.has(codigo)) continue;

      unicos.set(codigo, {
        codigo,
        etiqueta: aTexto(fila.motivo_label_es) ?? codigo,
      });
    }

    return [...unicos.values()];
  }
  /**
   * ¿Hay fallos de entrega que el usuario todavía no ha visto?
   *
   * Lo decide el servidor comparando con la última vez que este usuario abrió
   * la bitácora, así que el aviso es por persona y no por instalación.
   */
  async hayNovedades(): Promise<boolean> {
    const { data, error } = await supabase.rpc('v2_ops_tiene_mensajes_nuevos', {
      p_categoria: CATEGORIA,
    });

    if (error) {
      // Un aviso que no se puede consultar no debe romper la aplicación
      // entera: se calla y se sigue.
      registrarError('bitacora.hayNovedades', error);
      return false;
    }

    return data === true;
  }

  /** Deja constancia de que el usuario ya miró los fallos. */
  async marcarComoVistos(): Promise<void> {
    const { error } = await supabase.rpc('v2_ops_marcar_categoria_vista', {
      p_categoria: CATEGORIA,
    });

    if (error) registrarError('bitacora.marcarComoVistos', error);
  }
}

export const repositorioBitacora = new RepositorioBitacora();
